'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { usePathname } from 'next/navigation';
import { useConvexAuth } from 'convex/react';
import { AnimatePresence } from 'framer-motion';
import {
  beginPersistentVoiceCall,
  shouldEndPersistentVoiceCallForPath,
  type PersistentVoiceCall,
  type PersistentVoiceCallSource,
  type PendingPersistentVoiceCallSource,
} from '@/lib/persistent-voice-call-state';
import { consumeRealtimeVoiceNavigationIntent } from '@/lib/realtime-voice-navigation-intent';
import type { RealtimeVoicePageMetadata } from '@/lib/realtime-voice-page-context';
import { PersistentVoiceCallSession } from '@/components/voice/persistent-voice-call-session';

type PersistentVoiceCallContextValue = {
  call: PersistentVoiceCall | null;
  startCall: (
    source: PersistentVoiceCallSource | PendingPersistentVoiceCallSource,
    sessionId: string
  ) => 'started' | 'already-active' | 'different-thread-active';
  endCall: () => void;
  markCallEnding: (sessionId: string) => void;
  cancelPendingCall: (routeRef: string) => void;
  setPageMetadata: (metadata: RealtimeVoicePageMetadata | null) => void;
  pageMetadata: RealtimeVoicePageMetadata | null;
  setDockTarget: (target: HTMLElement | null) => void;
};

const PersistentVoiceCallContext =
  createContext<PersistentVoiceCallContextValue | null>(null);

export function PersistentVoiceCallProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  const { isAuthenticated, isLoading } = useConvexAuth();
  const [dockTarget, setDockTarget] = useState<HTMLElement | null>(null);
  const [call, setCall] = useState<PersistentVoiceCall | null>(null);
  const callRef = useRef<PersistentVoiceCall | null>(null);
  const [pageMetadata, setPageMetadata] =
    useState<RealtimeVoicePageMetadata | null>(null);

  useEffect(() => {
    const current = callRef.current;
    if (
      current &&
      (shouldEndPersistentVoiceCallForPath(pathname, current.orgSlug) ||
        (!isLoading && !isAuthenticated))
    ) {
      consumeRealtimeVoiceNavigationIntent(current);
      callRef.current = null;
      setCall(null);
    }
    setPageMetadata((current) =>
      current?.pathname === pathname ? current : null
    );
  }, [pathname, isLoading, isAuthenticated]);

  const startCall = useCallback(
    (
      source: PersistentVoiceCallSource | PendingPersistentVoiceCallSource,
      sessionId: string
    ) => {
      const result = beginPersistentVoiceCall(
        callRef.current,
        source,
        sessionId
      );
      if (result.outcome === 'started') {
        callRef.current = result.call;
        setCall(result.call);
      }
      return result.outcome;
    },
    []
  );
  const endCall = useCallback(() => {
    if (callRef.current) consumeRealtimeVoiceNavigationIntent(callRef.current);
    callRef.current = null;
    setCall(null);
  }, []);
  const markCallEnding = useCallback((sessionId: string) => {
    const current = callRef.current;
    if (!current || current.sessionId !== sessionId) return;
    const ending = { ...current, isEnding: true };
    callRef.current = ending;
    setCall(ending);
  }, []);
  const cancelPendingCall = useCallback(
    (routeRef: string) => {
      const current = callRef.current;
      if (current && !current.orgThreadId && current.routeRef === routeRef)
        endCall();
    },
    [endCall]
  );
  const value = useMemo(
    () => ({
      call,
      startCall,
      endCall,
      markCallEnding,
      cancelPendingCall,
      setPageMetadata,
      pageMetadata,
      setDockTarget,
    }),
    [call, startCall, endCall, markCallEnding, cancelPendingCall, pageMetadata]
  );

  return (
    <PersistentVoiceCallContext.Provider value={value}>
      {children}
      <AnimatePresence>
        {call ? (
          <PersistentVoiceCallSession
            key={call.sessionId}
            call={call}
            pathname={pathname}
            pageMetadata={pageMetadata}
            dockTarget={dockTarget}
            onEnd={endCall}
            onEnding={markCallEnding}
          />
        ) : null}
      </AnimatePresence>
    </PersistentVoiceCallContext.Provider>
  );
}

export function usePersistentVoiceCall() {
  const value = useContext(PersistentVoiceCallContext);
  if (!value)
    throw new Error(
      'usePersistentVoiceCall must be used within PersistentVoiceCallProvider'
    );
  return value;
}

export function usePersistentVoiceCallOptional() {
  return useContext(PersistentVoiceCallContext);
}
